import { useQuery } from "@tanstack/react-query";
import { AgentBaseFilters } from "../types/AgentBaseFilters";
import { AgentBaseResponse } from "../types/AgentBaseResponse";
import { get } from "@/shared/utils/fetchApi";

// Sem `selectedColumns`: o endpoint /agent-bases não aceita projeção de colunas,
// então a seleção de colunas é só de exibição — não entra na requisição nem no
// queryKey (entrar geraria refetch para dados idênticos).
const fetchAgentBase = async (
  filters: AgentBaseFilters,
): Promise<AgentBaseResponse> => {
  if (!filters.workspaceId) {
    console.warn("[useAgentBase] No workspaceId provided");
    return { data: [], total: 0, page: 1, pageSize: 10, total_pages: 0 };
  }
  const params = new URLSearchParams();

  params.append("product_id", filters.workspaceId);
  if (filters.page) params.append("page", filters.page.toString());
  if (filters.pageSize) params.append("page_size", filters.pageSize.toString());
  if (filters.search) params.append("search", filters.search);
  if (filters.type !== undefined)
    params.append("type", filters.type.toString());
  if (filters.includeAvailable !== undefined) {
    params.append("include_available", filters.includeAvailable.toString());
  }

  const endpoint = `/agent-bases`;
  const urlWithParams = params.toString()
    ? `${endpoint}?${params.toString()}`
    : endpoint;

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const list: any[] = await get<any[]>(urlWithParams);

    const total = list.length;
    const pageSize = filters.pageSize || 10;
    const page = filters.page || 1;
    const total_pages = Math.ceil(total / pageSize);

    return {
      data: list,
      total,
      page,
      pageSize,
      total_pages,
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error("[useAgentBase] Error fetching agents:", {
      error,
      message: error.message,
      response: error.response,
    });
    throw error;
  }
};

export function useAgentBase(
  filters: AgentBaseFilters = {},
  enabled: boolean = true,
) {
  return useQuery({
    // A chave contém só o que vai na requisição. Com o objeto `filters` inteiro,
    // campos que o endpoint não aceita (`filters` por facetas, `searchFieldType`,
    // `startDate`, `executor`) criavam entrada nova no cache e refetch para
    // exatamente a mesma resposta.
    queryKey: [
      "agent-base",
      filters.workspaceId,
      filters.page,
      filters.pageSize,
      filters.search,
      filters.type,
      filters.includeAvailable,
    ],
    queryFn: () => fetchAgentBase(filters),
    enabled: enabled && !!filters.workspaceId,
    staleTime: 1000 * 60 * 5, // 5 minutos
    retry: false,
  });
}
